import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { TabView, TabPanel } from 'primereact/tabview';
import { AttemptResultPieChart } from '../components/AttemptResultPieChart';
import { ModuleBreakdownBarChart, buildModuleBreakdown } from '../components/ModuleBreakdownBarChart';
import { SimuladoScoreTrendChart } from '../components/SimuladoScoreTrendChart';
import { DashboardService } from '../../../service/DashboardService';
import { ModuleService } from '../../../service/ModuleService';

const dashboardService = new DashboardService();
const moduleService = new ModuleService();

const studentDisplayName = (student) => {
    if (!student) return '';
    const name = [student.firstName, student.lastName].filter(Boolean).join(' ');
    return name || student.email;
};

const summarizeAttemptItems = (items) => {
    let correct = 0;
    let wrong = 0;
    let blank = 0;
    items.forEach(item => {
        if (item.selectedChoiceId == null) blank += 1;
        else if (item.isCorrect) correct += 1;
        else wrong += 1;
    });
    return { correct, wrong, blank, total: items.length };
};

// Espelha a tela de Estatísticas do app (QuizStatisticsPage) pra Quiz "por
// módulo" e "por prova" — pizza Acertos/Erros/Em branco sempre, e barra por
// módulo só na aba Prova (uma prova cruza vários módulos, um módulo não).
// Reaproveita getQuizAttemptDetail (mesma fonte do Gabarito no Timeline) —
// não existe estatística nova aqui, só uma forma diferente de pedir os
// dados (por módulo/prova escolhido no select, em vez de "a tentativa que
// acabei de abrir no timeline"). As opções do dropdown vêm do catálogo
// inteiro (mesmos endpoints usados nos filtros "por módulo"/"por prova" do
// resto do dashboard) — não do QuizHistory do aluno, que pode ter linhas
// duplicadas por bug de sincronização do app; defaultId (esse sim vindo do
// QuizHistory) só serve pra pré-selecionar o mais recente, e uma duplicata
// ali não teria efeito visível (é só o topo de um ORDER BY ... LIMIT 1).
const QuizStatsSection = ({ studentId, options, defaultId, moduleOrTest }) => {
    const [selectedId, setSelectedId] = useState(null);
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setSelectedId(defaultId ?? null);
    }, [defaultId]);

    useEffect(() => {
        if (selectedId == null) {
            setItems(null);
            return;
        }
        setLoading(true);
        setError(null);
        const params = moduleOrTest === 'module' ? { moduleId: selectedId } : { testId: selectedId };
        dashboardService.getQuizAttemptDetail(studentId, params)
            .then(res => setItems(res || []))
            .catch(() => setError('Não foi possível carregar as estatísticas.'))
            .finally(() => setLoading(false));
    }, [studentId, selectedId, moduleOrTest]);

    const summary = useMemo(() => (items ? summarizeAttemptItems(items) : null), [items]);
    const moduleBreakdown = useMemo(() => (items ? buildModuleBreakdown(items) : []), [items]);

    return (
        <div>
            <Dropdown
                value={selectedId}
                options={options}
                onChange={(e) => setSelectedId(e.value)}
                placeholder={moduleOrTest === 'module' ? 'Selecione um módulo' : 'Selecione uma prova'}
                filter
                showClear
                disabled={options.length === 0}
                className="w-full mb-3"
            />
            {selectedId == null ? (
                <p className="text-color-secondary">Selecione {moduleOrTest === 'module' ? 'um módulo' : 'uma prova'} pra ver o desempenho.</p>
            ) : loading ? (
                <div className="flex justify-content-center p-5">
                    <ProgressSpinner />
                </div>
            ) : error ? (
                <p>{error}</p>
            ) : summary && summary.total > 0 ? (
                <>
                    <p className="text-sm text-center mt-0 mb-2">
                        {summary.correct} certas / {summary.wrong} erradas / {summary.blank} em branco
                        {' '}(de {summary.total})
                    </p>
                    {moduleOrTest === 'test' ? (
                        <TabView>
                            <TabPanel header="Resumo">
                                <AttemptResultPieChart correct={summary.correct} wrong={summary.wrong} blank={summary.blank} />
                            </TabPanel>
                            {moduleBreakdown.length >= 2 && (
                                <TabPanel header="Por módulo">
                                    <ModuleBreakdownBarChart data={moduleBreakdown} />
                                </TabPanel>
                            )}
                        </TabView>
                    ) : (
                        <AttemptResultPieChart correct={summary.correct} wrong={summary.wrong} blank={summary.blank} />
                    )}
                </>
            ) : (
                <p className="text-color-secondary">Nenhuma questão respondida encontrada.</p>
            )}
        </div>
    );
};

export const StudentCompetenciesPage = () => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    // A linha da grid de Ranking já tem nome/serviço/UF — chega pelo state
    // da navegação (history.push(path, { student: rowData })) e usamos como
    // valor inicial, sem esperar a resposta de /mirror pra mostrar o
    // cabeçalho. Continua buscando /mirror mesmo assim (abaixo), pra
    // funcionar também em reload direto da URL, onde esse state não existe.
    const [student, setStudent] = useState(location.state?.student ?? null);
    const [simuladoScoreHistory, setSimuladoScoreHistory] = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [testOptions, setTestOptions] = useState([]);
    const [defaultModuleId, setDefaultModuleId] = useState(null);
    const [defaultTestId, setDefaultTestId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = () => {
        setError(null);

        // Nome/serviço do cabeçalho atualizam assim que essa chamada
        // responder, sem esperar o resto — antes tudo dependia de um único
        // Promise.all, então o cabeçalho ficava em branco até as 4 outras
        // chamadas (mais lentas) terminarem também.
        dashboardService.getStudentMirror(id)
            .then(mirror => {
                setStudent(mirror.student || null);
                setSimuladoScoreHistory(mirror.simuladoScoreHistory || []);
            })
            .catch(() => setError('Não foi possível carregar o espelhamento do aluno.'));

        setLoading(true);
        Promise.all([
            // Catálogo inteiro (mesmos endpoints dos filtros "por módulo"/
            // "por prova" já usados no resto do dashboard) — não o
            // QuizHistory do aluno, que pode ter linhas duplicadas.
            moduleService.getModules(0, 0, 500, 'description', 1),
            dashboardService.getTestsForFilter(),
            // Só pra saber qual foi o mais recente e pré-selecionar.
            dashboardService.getStudentQuizModules(id),
            dashboardService.getStudentQuizTests(id),
        ])
            .then(([modulesRes, tests, recentModules, recentTests]) => {
                setModuleOptions((modulesRes.data || []).map(m => ({ label: m.description, value: m.id })));
                setTestOptions((tests || []).map(t => ({
                    label: t.year ? `${t.title} (${t.year})` : t.title,
                    value: t.id
                })));
                setDefaultModuleId(recentModules?.[0]?.moduleId ?? null);
                setDefaultTestId(recentTests?.[0]?.testId ?? null);
            })
            .catch(() => setError('Não foi possível carregar o espelhamento do aluno.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        setStudent(location.state?.student ?? null);
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return (
        <div className="grid p-fluid">
            <div className="col-12">
                <div className="card">
                    <div className="flex align-items-center justify-content-between gap-3 mb-4">
                        <div className="flex align-items-center gap-3">
                            <Button
                                icon="pi pi-arrow-left"
                                rounded
                                text
                                // goBack() (POP) em vez de push('/students-ranking') — é
                                // o que deixa a tabela de ranking (StudentsRankingTable)
                                // saber que isso foi um "voltar" e não uma navegação
                                // nova pelo menu, pra decidir se reaproveita o estado ou
                                // reseta (mesmo padrão de UsersView.js/UserDetailsPage.js
                                // e StudentTimelinePage.js).
                                onClick={() => history.goBack()}
                            />
                            <div>
                                <h5 className="m-0">{studentDisplayName(student)}</h5>
                                {student && (
                                    <span className="text-color-secondary text-sm">
                                        {[student.serviceName, student.uf].filter(Boolean).join(' — ') || student.email}
                                    </span>
                                )}
                            </div>
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            loading={loading}
                            onClick={fetchData}
                        />
                    </div>

                    {loading ? (
                        <div className="flex justify-content-center p-5">
                            <ProgressSpinner />
                        </div>
                    ) : error ? (
                        <p>{error}</p>
                    ) : (
                        <Accordion multiple activeIndex={[0, 1, 2]}>
                            <AccordionTab header="Simulado — Evolução de Score (últimos 10)">
                                {simuladoScoreHistory.length === 0 ? (
                                    <p className="text-color-secondary">Nenhum simulado registrado ainda.</p>
                                ) : (
                                    <SimuladoScoreTrendChart data={simuladoScoreHistory} />
                                )}
                            </AccordionTab>
                            <AccordionTab header="Quiz — Questões por Prova">
                                <QuizStatsSection
                                    studentId={id}
                                    options={testOptions}
                                    defaultId={defaultTestId}
                                    moduleOrTest="test"
                                />
                            </AccordionTab>
                            <AccordionTab header="Quiz — Questões por Módulo">
                                <QuizStatsSection
                                    studentId={id}
                                    options={moduleOptions}
                                    defaultId={defaultModuleId}
                                    moduleOrTest="module"
                                />
                            </AccordionTab>
                        </Accordion>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentCompetenciesPage;
